<template>
  <div class="locationpropertydetails">
    <v-layout row wrap>
      <v-flex xs12 sm6>
        <v-layout row wrap>
          <v-flex xs12>
            <NumberInputCommon
              v-model="value.yearBuilt"
              :maxlength="InputValidations.propertyYearBuilt.max"
              :validations="InputValidations.propertyYearBuilt"
              display-name="Year Built"
              ref="yearBuilt" />
          </v-flex>

          <v-flex xs12>
            <NumberInputCommon
              v-model="value.squareFootage"
              :maxlength="InputValidations.propertySquareFootage.max"
              :validations="InputValidations.propertySquareFootage"
              display-name="Square Footage"
              ref="squareFootage"
              suffix="sqft" />
          </v-flex>

          <v-flex xs12>
            <NumberInputCommon
              v-model="value.bedrooms"
              :maxlength="InputValidations.propertyBedrooms.max"
              :validations="InputValidations.propertyBedrooms"
              display-name="Bedrooms"
              ref="bedrooms" />
          </v-flex>

          <v-flex xs12>
            <NumberInputCommon
              v-model="value.bathrooms"
              :maxlength="InputValidations.propertyFullBathrooms.max"
              :validations="InputValidations.propertyFullBathrooms"
              display-name="Full Bathrooms"
              ref="fullBathrooms" />
          </v-flex>

          <v-flex xs12>
            <NumberInputCommon
              v-model="value.halfBathrooms"
              :maxlength="InputValidations.propertyHalfBathrooms.max"
              :validations="InputValidations.propertyHalfBathrooms"
              display-name="Half Bathrooms"
              ref="halfBathrooms" />
          </v-flex>

          <v-flex xs12>
            <TextInputCommon
              v-model="value.mlsNumber"
              :maxlength="InputValidations.propertyMLS.max"
              :validations="InputValidations.propertyMLS"
              display-name="MLS #"
              ref="mlsNumber" />
          </v-flex>
          <v-flex
            v-for="field in visibleFields"
            xs12
            :key="`${field.id}`">
            <TextInputCommon
              v-model="value[field.id]"
              :display-name="`${field.name}`"
              :maxlength="125"
              :validations="{ required: field.required, max: 125}"
              :hint="field.description"
              :persistent-hint="true"
              v-if="field.type === 'text'"/>
            <NumberInputCommon
              v-model="value[field.id]"
              :display-name="`${field.name}`"
              :validations="{ required: field.required, numeric: true }"
              :maxlength="6"
              :hint="field.description"
              :persistent-hint="true"
              v-if="field.type === 'number'"/>
            <v-textarea
              v-model="value[field.id]"
              :maxlength="225"
              :counter="225"
              v-validate="{ required: field.required, max: 225 }"
              :error="errors.collect(field.name).length > 0"
              :label="`${field.name}${field.required ? ' *' : ''}`"
              :data-vv-name="field.name"
              :hint="field.description"
              :persistent-hint="true"
              v-if="field.type === 'textarea'"
              outline />
            <v-menu
              v-if="field.type === 'date'"
              v-model="dateMenus[`${field.id}`]"
              :close-on-content-click="false"
              :nudge-right="40"
              lazy
              transition="scale-transition"
              offset-y
              full-width
              max-width="290px"
              min-width="290px">
              <template v-slot:activator="{on}">
                <TextInputCommon
                  v-model="value.formattedDates[field.id]"
                  append-icon="event"
                  v-if="field.type === 'date'"
                  :error="errors.collect(field.id).length > 0"
                  :display-name="`${field.name}${field.required ? ' *' : ''}`"
                  @input="handleDateTextInput($event, field)"
                  :hint="field.description"
                  :persistent-hint="true"
                  v-on="on"/>
              </template>
              <v-date-picker
                v-model="value[field.id]"
                no-title
                @input="handleDatePicked($event, field)"></v-date-picker>
              </v-menu>
              <!-- Hidden text element to validate date-picker -->
              <TextInputCommon
                  v-model="value.formattedDates[field.id]"
                  append-icon="event"
                  v-show="false"
                  :validations="{ required: field.required, date_format: 'MM-dd-yyyy' }"
                  :data-vv-name="field.id"
                  :identifier="field.id"
                  v-if="field.type === 'date'"
                  :display-name="field.name"/>
          </v-flex>

        </v-layout>
      </v-flex>

      <v-flex xs12 sm6>
        <v-layout row wrap>
          <v-flex xs12>
            <v-layout row align-center class="waterUtility">
              <v-flex shrink py-1>
                <v-switch
                v-model="value.isWaterOn"></v-switch>
              </v-flex>
              <v-flex grow class="label">
                Water Utility is on
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12>
            <v-layout row align-center class="electricUtility">
              <v-flex shrink py-0>
                <v-switch
                v-model="value.isPowerOn"></v-switch>
              </v-flex>
              <v-flex grow class="label">
                Electricity Utility is on
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12>
            <v-layout row align-center class="gasUtility">
              <v-flex shrink py-0>
                <v-switch
                v-model="value.isGasOn"></v-switch>
              </v-flex>
              <v-flex grow class="label">
                Gas Utility is on
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { Constants } from '@homegauge/vue';
import Moment from 'moment';
import NumberInputCommon from '@/components/common/NumberInputCommon.vue';
import TextInputCommon from '@/components/common/TextInputCommon.vue';

const { InputValidations } = Constants;

const DEFAULT_MODEL = {
  isGasOn: false,
  isPowerOn: false,
  isWaterOn: false,
  yearBuilt: null,
  squareFootage: null,
  bedrooms: null,
  bathrooms: null,
  halfBathrooms: null,
  mlsNumber: null,
  formattedDates: {},
};

export default {
  components: {
    NumberInputCommon,
    TextInputCommon,
  },
  inject: ['$validator'],
  props: {
    value: {
      type: Object,
      default: () => DEFAULT_MODEL,
    },
    customFields: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      InputValidations,
      dateMenus: {},
      formattedDates: {},
    };
  },
  mounted() {
    this.dateMenus = this.visibleFields.reduce((acc, field) => {
      if (field.type === 'date') {
        acc[field.id] = false;
      }
      return acc;
    }, {});
    this.value.formattedDates = this.visibleFields.reduce((acc, field) => {
      if (field.type === 'date') {
        acc[field.id] = this.value[field.id] ? Moment(this.value[field.id]).format('MM-DD-YYYY') : '';
      }
      return acc;
    }, {});
  },
  methods: {
    handleDatePicked(date, field) {
      this.value[field.id] = date;
      this.dateMenus[field.id] = false;
      this.value.formattedDates[field.id] = Moment(date).format('MM-DD-YYYY');
    },
    handleDateTextInput(value, field) {
      if (!value) {
        this.value[field.id] = null;
      }
    },
  },
  computed: {
    visibleFields() {
      return this.customFields.filter(field => field.displayStatus === 'visible');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/vars/typography';

small {
  display: block;
  font-size: 0.85rem;
  font-weight: $typography-weight-semibold;
  line-height: 1.6;
}

.v-input--selection-controls {
  // margin: 0;
  // padding: 0;

}

.locationpropertydetails {
  &--subheading {
    letter-spacing: 0.1rem;
  }
}
</style>
